/**
 * Analytics Service
 * 
 * Aggregates and provides analytics data for trips and users
 */

import { collection, doc, getDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Trip } from './trips';
import type { ItineraryItem } from './itinerary';
import type { Expense } from './expenses';
import { fetchTripItineraryItems } from './itinerary';

/**
 * Calculate distance between two coordinates (Haversine formula)
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export type TripAnalytics = {
  tripId: string;
  totalDistance: number; // km
  countriesVisited: string[];
  citiesVisited: string[];
  totalSpending: number;
  spendingByCategory: {
    flights: number;
    lodging: number;
    food: number;
    activities: number;
    transport: number;
    other: number;
  };
  timeSpent: {
    [city: string]: number; // days
  };
  mostVisitedPlaces: Array<{
    placeId: string;
    name: string;
    visitCount: number;
  }>;
  travelPatterns: {
    averageDailySpending: number;
    preferredTravelStyle: string[];
    favoriteCategories: string[];
  };
  lastUpdated: Date;
};

export type UserAnalytics = {
  userId: string;
  totalTrips: number;
  totalDistance: number; // km
  countriesVisited: string[];
  totalSpending: number;
  favoriteDestinations: Array<{ destination: string; count: number }>;
  travelStyle: string[];
  averageTripDuration: number; // days
  annualReport: {
    year: number;
    trips: number;
    distance: number;
    spending: number;
  };
  lastUpdated: Date;
};

type FirestoreTripAnalytics = Omit<TripAnalytics, 'lastUpdated'> & {
  lastUpdated: any; // Firestore Timestamp
};

type FirestoreUserAnalytics = Omit<UserAnalytics, 'lastUpdated'> & {
  lastUpdated: any; // Firestore Timestamp
};

const TRIP_ANALYTICS_COLLECTION = 'tripAnalytics';
const USER_ANALYTICS_COLLECTION = 'userAnalytics';

/**
 * Get analytics for a specific trip
 */
export const getTripAnalytics = async (tripId: string): Promise<TripAnalytics | null> => {
  try {
    const analyticsRef = doc(db, TRIP_ANALYTICS_COLLECTION, tripId);
    const analyticsDoc = await getDoc(analyticsRef);

    if (!analyticsDoc.exists()) {
      // Analytics not yet calculated, return null
      return null;
    }

    const data = analyticsDoc.data() as FirestoreTripAnalytics;
    return {
      ...data,
      lastUpdated: data.lastUpdated?.toDate?.() || new Date(data.lastUpdated),
    };
  } catch (error) {
    console.error('[Analytics] Error getting trip analytics:', error);
    return null;
  }
};

/**
 * Get analytics for a user
 */
export const getUserAnalytics = async (userId: string): Promise<UserAnalytics | null> => {
  try {
    const analyticsRef = doc(db, USER_ANALYTICS_COLLECTION, userId);
    const analyticsDoc = await getDoc(analyticsRef);

    if (!analyticsDoc.exists()) {
      return null;
    }

    const data = analyticsDoc.data() as FirestoreUserAnalytics;
    return {
      ...data,
      lastUpdated: data.lastUpdated?.toDate?.() || new Date(data.lastUpdated),
    };
  } catch (error) {
    console.error('[Analytics] Error getting user analytics:', error);
    return null;
  }
};

/**
 * Calculate trip analytics from trip data
 * This is a client-side calculation that can be used as a fallback
 */
export const calculateTripAnalytics = async (
  trip: Trip,
  itineraryItems: ItineraryItem[],
  expenses: Expense[]
): Promise<TripAnalytics> => {
  // Calculate total distance
  let totalDistance = 0;
  const coordinates: Array<{ latitude: number; longitude: number }> = [];
  
  itineraryItems.forEach((item) => {
    if (item.coordinates) {
      coordinates.push(item.coordinates);
    }
  });

  // Calculate distance between consecutive points
  for (let i = 1; i < coordinates.length; i++) {
    const dist = calculateDistance(
      coordinates[i - 1].latitude,
      coordinates[i - 1].longitude,
      coordinates[i].latitude,
      coordinates[i].longitude
    );
    totalDistance += dist;
  }

  // Extract countries and cities from itinerary
  const countriesSet = new Set<string>();
  const citiesSet = new Set<string>();
  const cityTimeSpent: Record<string, number> = {};
  const placeVisits: Record<string, { name: string; count: number }> = {};

  itineraryItems.forEach((item) => {
    if (item.location) {
      // Try to extract city and country from location
      const parts = item.location.split(',').map(s => s.trim());
      if (parts.length > 1) {
        const city = parts[0];
        const country = parts[parts.length - 1];
        citiesSet.add(city);
        countriesSet.add(country);
        
        // Track time spent in each city (rough estimate: 1 item = 0.5 days)
        cityTimeSpent[city] = (cityTimeSpent[city] || 0) + 0.5;
      }

      // Track place visits
      const placeKey = item.location;
      if (!placeVisits[placeKey]) {
        placeVisits[placeKey] = { name: item.title || item.location, count: 0 };
      }
      placeVisits[placeKey].count += 1;
    }
  });

  // Calculate spending by category
  const spendingByCategory = {
    flights: 0,
    lodging: 0,
    food: 0,
    activities: 0,
    transport: 0,
    other: 0,
  };

  let totalSpending = 0;
  expenses.forEach((expense) => {
    const category = expense.category || 'other';
    const amount = expense.amount;
    
    if (category in spendingByCategory) {
      spendingByCategory[category as keyof typeof spendingByCategory] += amount;
    } else {
      spendingByCategory.other += amount;
    }
    totalSpending += amount;
  });

  // Calculate travel patterns
  const tripDuration = Math.ceil(
    (trip.endDate.getTime() - trip.startDate.getTime()) / (1000 * 60 * 60 * 24)
  ) || 1;
  const averageDailySpending = totalSpending / tripDuration;

  // Get favorite categories from expenses
  const categoryCounts: Record<string, number> = {};
  expenses.forEach((expense) => {
    const cat = expense.category || 'other';
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  });
  const favoriteCategories = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([cat]) => cat);

  // Get most visited places
  const mostVisitedPlaces = Object.entries(placeVisits)
    .map(([placeId, data]) => ({
      placeId,
      name: data.name,
      visitCount: data.count,
    }))
    .sort((a, b) => b.visitCount - a.visitCount)
    .slice(0, 10);

  return {
    tripId: trip.id,
    totalDistance,
    countriesVisited: Array.from(countriesSet),
    citiesVisited: Array.from(citiesSet),
    totalSpending,
    spendingByCategory,
    timeSpent: cityTimeSpent,
    mostVisitedPlaces,
    travelPatterns: {
      averageDailySpending,
      preferredTravelStyle: [], // Would need to be extracted from trip metadata
      favoriteCategories,
    },
    lastUpdated: new Date(),
  };
};

